import { useState } from "react";
import { Card, IconButton } from "@mui/material";
import { Edit, Delete as DeleteIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { loadMemberReportingsPageFromBackend, deleteMemberReporting } from "app/services/member.service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { isEmpty } from "utils/validationUtil";

import { memberReportingPageActions } from "store/member-reporting-page-slice";
import { useServerPaginatedGrid } from "app/hooks/useServerPaginatedGrid";
import { PaginatedDataGrid } from "app/components/common/PaginatedDataGrid";
import AddMemberDialog from "./AddMemberReportingDialog";
import { ConfirmDialog } from "../ConfirmDialog";

// Only the reporting row's own id is sortable server-side; member/manager/client are enriched.
const SORT_FIELD_MAP = {
  id: "id",
};

const AllMemberReportingsView = () => {
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const {
    rows,
    totalElements,
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchQuery,
    loading,
    fetchPage,
    handlePaginationModelChange,
    handleSortModelChange,
    handleSearchChange,
  } = useServerPaginatedGrid({
    selector: (state) => state.memberReportingPage,
    actions: memberReportingPageActions,
    fetchFn: loadMemberReportingsPageFromBackend,
  });

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    // A deleted member leaves the reporting row without a member object, so
    // read the name defensively and delete by the row's own memberId.
    const memberName = data.member?.name ?? "deleted member";
    setConfirmationMessage(`Are you sure you want to delete this Client Member Reporting (Name: '${memberName}')?`);
    setDeleteId(data.memberId);
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteMemberReporting(deleteId)
      .then(() => fetchPage())
      .catch((error) => handleAxiosError(error));
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => {
        // A deleted member leaves the reporting row without a member; block the edit instead.
        const memberMissing = isEmpty(obj.row.member);
        return [
          <IconButton
            aria-label="Edit"
            onClick={() => editEventHandler(obj.row)}
            color="inherit"
            disabled={memberMissing}
            title={memberMissing ? "Member no longer exists" : undefined}
          >
            <Edit />
          </IconButton>,
          <IconButton aria-label="Delete" onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
            <DeleteIcon />
          </IconButton>,
        ];
      },
    },
    { field: "id", headerName: "Member Id", width: 120 },
    {
      field: "name",
      headerName: "Member Name",
      width: 350,
      sortable: false,
      valueGetter: (value, row) => (row.member ? `${row.member.name ?? ""} (${row.member.email ?? ""})` : "—"),
    },
    { field: "manager_name", headerName: "Manager", width: 300, sortable: false, valueGetter: (value, row) => row.manager?.name ?? "—" },
    { field: "client_code", headerName: "Client / Account", width: 150, sortable: false, valueGetter: (value, row) => row.client?.code ?? "—" },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            InOpTra's Team Member - Manager Mapping
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <PaginatedDataGrid
            rows={rows}
            columns={columns}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDirection={sortDirection}
            searchQuery={searchQuery}
            isLoading={loading}
            sortFieldMap={SORT_FIELD_MAP}
            defaultSortBy="id"
            defaultSortDirection="ASC"
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            onSearchChange={handleSearchChange}
            onAdd={() => {
              setEditObject(null);
              setOpen(true);
            }}
            addLabel="Add InOpTra's Team Member Reporting"
            columnVisibilityModel={{ id: false }}
          />
        </MDBox>
      </Card>
      <AddMemberDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={() => fetchPage()}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This Member Reporting?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </div>
  );
};

export default AllMemberReportingsView;
